import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppState } from '../../contexts/AppStateContext';
import FileUpload from '../common/FileUpload';
import ToolHeader from '../common/ToolHeader';
import ProgressBar from '../common/ProgressBar';
import { loadImage, convertImageFormat } from '../../utils/imageUtils';
import { formatFileSize, downloadFile } from '../../utils/fileUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiLoader, FiInfo } = FiIcons;

const ImageConvertTool = () => {
  const { t } = useLanguage();
  const { state, dispatch } = useAppState();
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  useEffect(() => {
    if (state.selectedFile && state.selectedFile.type.startsWith('image/')) {
      loadImagePreview();
    }
  }, [state.selectedFile]);

  const loadImagePreview = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      setProgress(20);
      setProgressMessage('載入圖片...');

      const img = await loadImage(state.selectedFile);
      const url = URL.createObjectURL(state.selectedFile);

      setProgress(100);
      setProgressMessage('完成');

      dispatch({
        type: 'SET_IMAGE_PREVIEW',
        payload: {
          url,
          width: img.naturalWidth,
          height: img.naturalHeight
        }
      });

      dispatch({ type: 'SET_LOADING', payload: false });
      setProgress(0);
      setProgressMessage('');
    } catch (error) {
      console.error('圖片加載錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
      setProgress(0);
      setProgressMessage('');
    }
  };

  const handleFileUpload = (file) => {
    if (!file.type.startsWith('image/')) {
      if (window.showToast) {
        window.showToast(t('invalidFileType'), 'error');
      }
      return;
    }
    dispatch({ type: 'SET_SELECTED_FILE', payload: file });
  };

  const handleConvert = async () => {
    if (!state.selectedFile) {
      if (window.showToast) {
        window.showToast(t('pleaseUploadFile'), 'warning');
      }
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      setProgress(10);
      setProgressMessage('開始轉換...');

      const img = await loadImage(state.selectedFile);
      
      setProgress(50);
      setProgressMessage('轉換格式中...');

      const blob = await convertImageFormat(
        img,
        state.imageFormat,
        state.imageQuality / 100
      );

      setProgress(90);
      setProgressMessage('生成文件...');

      const url = URL.createObjectURL(blob);

      dispatch({
        type: 'SET_CONVERTED_IMAGE',
        payload: {
          blob,
          url,
          size: blob.size
        }
      });

      setProgress(100);
      setProgressMessage('完成');

      if (window.showToast) {
        window.showToast(t('conversionSuccess'), 'success');
      }
    } catch (error) {
      console.error('轉換錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message, 'error');
      }
    } finally {
      setTimeout(() => {
        setProgress(0);
        setProgressMessage('');
      }, 500);
    }
  };

  const handleDownload = () => {
    if (state.convertedImageBlob) {
      const extension = state.imageFormat === 'image/jpeg' ? 'jpg' :
                       state.imageFormat === 'image/png' ? 'png' : 'webp';
      const originalName = state.selectedFile.name.split('.')[0];
      downloadFile(state.convertedImageBlob, `${originalName}-converted.${extension}`);
    }
  };

  const getFormatInfo = (format) => {
    const info = {
      'image/jpeg': {
        name: 'JPG',
        description: '高壓縮率,適合照片',
        features: ['小文件大小', '廣泛支持', '有損壓縮']
      },
      'image/png': {
        name: 'PNG',
        description: '無損壓縮,支持透明',
        features: ['無損質量', '支持透明度', '較大文件']
      },
      'image/webp': {
        name: 'WEBP',
        description: '現代格式,最佳壓縮',
        features: ['最佳壓縮', '支持透明度', '現代瀏覽器']
      }
    };
    return info[format] || info['image/jpeg'];
  };

  if (state.showResults && state.convertedImageUrl) {
    const savedSpace = state.originalSize - state.processedSize;
    const ratio = Math.round((savedSpace / state.originalSize) * 100);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ToolHeader title={t('conversionResults')} />

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-3">
                {t('originalImage')}
              </h4>
              <img
                src={state.imagePreview}
                alt="Original"
                className="w-full h-64 object-contain bg-gray-100 rounded-lg border"
              />
              <p className="text-sm text-gray-600 mt-2">
                {formatFileSize(state.originalSize)}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-600 mb-3">
                {t('convertedImage')}
              </h4>
              <img
                src={state.convertedImageUrl}
                alt="Converted"
                className="w-full h-64 object-contain bg-gray-100 rounded-lg border"
              />
              <p className="text-sm text-gray-600 mt-2">
                {formatFileSize(state.processedSize)}
              </p>
            </div>
          </div>

          {savedSpace !== 0 && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 mb-6 border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiInfo} className="text-blue-600" />
                <h4 className="font-semibold text-blue-800">文件大小變化</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/60 rounded-lg p-3">
                  <span className="text-sm text-gray-600">大小變化:</span>
                  <span className={`ml-2 font-semibold ${savedSpace > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {savedSpace > 0 ? '-' : '+'}{formatFileSize(Math.abs(savedSpace))}
                  </span>
                </div>
                <div className="bg-white/60 rounded-lg p-3">
                  <span className="text-sm text-gray-600">變化率:</span>
                  <span className={`ml-2 font-semibold ${savedSpace > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                    {savedSpace > 0 ? '-' : '+'}{Math.abs(ratio)}%
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <motion.button
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 shadow-md"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
            >
              <SafeIcon icon={FiDownload} />
              {t('downloadImage')}
            </motion.button>

            <motion.button
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: 'RESET_TOOL' })}
            >
              {t('startOver')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <ToolHeader
        title={t('imageConvert')}
        filename={state.selectedFile?.name}
      />

      <div className="p-6">
        <p className="text-gray-600 mb-6">
          {t('imageConvertDesc')}
        </p>

        {!state.showSettings ? (
          <FileUpload
            onFileUpload={handleFileUpload}
            acceptedTypes="image/*"
            uploadText={t('dropOrClickImage')}
            supportText={t('supportImageFormats')}
          />
        ) : (
          <div className="space-y-6">
            {state.imagePreview && (
              <div className="text-center">
                <img
                  src={state.imagePreview}
                  alt="Preview"
                  className="max-w-full max-h-64 mx-auto rounded-lg border"
                />
                <p className="text-sm text-gray-600 mt-2">
                  {state.originalWidth} × {state.originalHeight} {t('pixels')}
                </p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-4">
                {t('conversionSettings')}
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('targetFormat')}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {['image/jpeg', 'image/png', 'image/webp'].map((format) => {
                      const info = getFormatInfo(format);
                      return (
                        <motion.div
                          key={format}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            state.imageFormat === format
                              ? 'border-purple-500 bg-purple-50'
                              : 'border-gray-200 hover:border-purple-300'
                          }`}
                          onClick={() => dispatch({
                            type: 'UPDATE_SETTINGS',
                            payload: { imageFormat: format }
                          })}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <h5 className="font-semibold text-gray-800 mb-1">
                            {info.name}
                          </h5>
                          <p className="text-xs text-gray-600 mb-2">
                            {info.description}
                          </p>
                          <ul className="text-xs text-gray-500 space-y-1">
                            {info.features.map((feature, idx) => (
                              <li key={idx}>• {feature}</li>
                            ))}
                          </ul>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {state.imageFormat !== 'image/png' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('qualityJpgWebp')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min="1"
                        max="100"
                        value={state.imageQuality}
                        onChange={(e) => dispatch({
                          type: 'UPDATE_SETTINGS',
                          payload: { imageQuality: parseInt(e.target.value) }
                        })}
                        className="flex-1"
                      />
                      <span className="text-sm text-gray-600 w-12">
                        {state.imageQuality}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {state.imageQuality < 50 ? '低質量 (小文件)' :
                       state.imageQuality < 80 ? '中等質量 (推薦)' :
                       '高質量 (大文件)'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <AnimatePresence>
              {state.isLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-purple-50 rounded-lg p-4"
                >
                  <ProgressBar
                    progress={progress}
                    message={progressMessage}
                    showPercentage={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3 justify-center">
              <motion.button
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                whileHover={{ scale: state.isLoading ? 1 : 1.02 }}
                whileTap={{ scale: state.isLoading ? 1 : 0.98 }}
                onClick={handleConvert}
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    {t('convertingPleaseWait')}
                  </>
                ) : (
                  t('convertImage')
                )}
              </motion.button>

              <motion.button
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => dispatch({ type: 'RESET_TOOL' })}
              >
                {t('reuploadImage')}
              </motion.button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ImageConvertTool;